import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserCheck } from 'lucide-react';
import api from '../../../../api';
import { updateDossier } from '../../api/forumAPI';
import { useNotification } from '../../../../context/NotificationContext';
import CustomSelect from '../../../../components/ui/CustomSelect';

export default function AssignMandatoryModal({ isOpen, onClose, dossierId, roleType, currentValue, onSuccess }) {
  const { showToast } = useNotification();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(currentValue?.id || '');

  useEffect(() => {
    if (isOpen) {
      setSelectedValue(currentValue?.id || '');
      fetchOptions();
    }
  }, [isOpen, currentValue]);

  const fetchOptions = async () => {
    try {
      if (roleType === 'avocat') {
        const res = await api.get('/partenaires'); 
        const list = Array.isArray(res.data) ? res.data : [];
        setOptions(list.map(p => ({ label: p.displayName || p.name || 'Inconnu', value: p.id })));
      } else {
        const res = await api.get('/utilisateurs/members');
        setOptions(res.data.map(m => ({ label: `${m.Nom} (${m.Role})`, value: m.id })));
      }
    } catch (error) {
      console.error("Erreur chargement options", error);
      showToast("Erreur lors du chargement des choix", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {};
      if (roleType === 'referent') payload.referentMembershipId = selectedValue;
      if (roleType === 'decideur') payload.decideurMembershipId = selectedValue;
      if (roleType === 'avocat') payload.avocatPartyId = selectedValue;

      await updateDossier(dossierId, payload);
      showToast("Intervenant assigné avec succès", "success");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Erreur assignation", error);
      showToast("Erreur lors de l'assignation", "error");
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = () => {
    switch (roleType) {
      case 'referent': return 'Référent Client';
      case 'decideur': return 'Décideur';
      case 'avocat': return 'Avocat';
      default: return 'Intervenant';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="relative bg-surface rounded-xl shadow-hp-xl border border-workspace w-full max-w-md overflow-hidden flex flex-col z-10"
          >
            <div className="flex justify-between items-center p-6 border-b border-workspace bg-surface-muted">
              <h3 className="text-lg font-bold text-primary flex items-center">
                <UserCheck className="w-5 h-5 mr-2 text-brand" />
                Assigner : {getRoleLabel()}
              </h3>
              <button onClick={onClose} className="text-tertiary hover:text-primary transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-primary mb-1">
                  Sélectionnez {roleType === 'avocat' ? "un contact externe" : "un utilisateur interne"}
                </label>
                <CustomSelect
                  value={selectedValue}
                  onChange={setSelectedValue}
                  options={options}
                  placeholder={`Choisir un ${getRoleLabel().toLowerCase()}...`}
                  className="w-full"
                />
                {options.length === 0 && (
                  <p className="text-xs text-tertiary mt-2">Aucun choix disponible. Veuillez en créer un dans la section dédiée.</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-workspace">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !selectedValue}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Enregistrement...
                    </span>
                  ) : 'Enregistrer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
